import { Button } from "../../../../components/ui/button";

import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "../../../../components/ui/form";
import { useToast } from "../../../../hooks/use-toast";
import { FormInput } from "../../../../components/form-components/form-input.components";
import { useCallback, useEffect, useState } from "react";
import { debounce, handleHttpError } from "../../../../utils/utils";
import { useCreatePostAdminHttp } from "../../../../hooks/http/admin/posts/create-post.admin.http";
import { useParams } from "react-router-dom";
import Spinner from "../../../../components/spinner.components";

import { InstagramIcon } from "../../../../assets/svgs/common/instagram.svg";
import millify from "millify";
import { Input } from "../../../../components/ui/input";
import { ExclamationIcon } from "../../../../assets/svgs/common/exlamation.svg";
import { useAddSocialAdminHttp } from "../../../../hooks/http/admin/socials/add-social.http";
import { SheetContent, SheetTitle } from "../../../../components/ui/sheet";
import { Plus, X, } from "lucide-react";
import { useSearchVendorsHttp } from "../../../../hooks/http/admin/vendors/search-vendors.admin.http";
import { FormSearch } from "../../../../components/form-components/form-api-search.components";
import type { IVendor } from "../../../../types/models/vendor.modals.types";
import CheckIcon from "../../../../assets/svgs/common/check.svg";
import {
  type IPostDetail,
  useGetInstagramPostDetailsAdminHttp,
} from "../../../../hooks/http/admin/campaigns/utils/get-instagram-post-details.admin.http";
import { PostStatus } from "../../../../types/models/post.models.types";
import { SocialPlatforms } from "../../../../types/models/social.models.types";

const PostSchema = z.object({
  post_link: z.string().url("Please provide a valid post link"),
  fixed_payout: z.coerce.number().optional(),
  vendorId: z.string().optional(),
});

const FormSchema = z.object({
  posts: z.array(PostSchema).min(1, { message: "Add at least one post" }),
});

type PostData = z.infer<typeof PostSchema>;
type FormData = z.infer<typeof FormSchema>;

type AppProps = {
  reset: boolean;
};

export const AddPostSliderPartial: React.FunctionComponent<AppProps> = ({
  reset,
}) => {
  const { toast } = useToast();
  const { campaignId } = useParams();
  const [postDetails, setPostDetails] = useState<Record<number, IPostDetail>>(
    {}
  );
  const [vendors, setVendors] = useState<IVendor[]>();
  const [socialNotOnSolo, setSocialNotOnSolo] = useState<
    Record<number, boolean>
  >({});

  // Add this new state to track post success
  const [postSuccess, setPostSuccess] = useState<Record<number, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    execute: searchVendorsHttp,
    fetching: searchingVendors,
    cancel: cancelSearchVendors,
  } = useSearchVendorsHttp();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const searchVendors = useCallback(
    debounce(async (searchTerm: string) => {
      cancelSearchVendors();

      if (searchTerm.trim().length === 0) {
        cancelSearchVendors();
        setVendors([]);
        return;
      }

      try {
        const response = await searchVendorsHttp({
          body: null,
          queryParams: searchTerm.trim()
            ? { searchString: searchTerm }
            : undefined,
        });

        if (!response.success) {
          return handleHttpError(response, toast);
        }

        setVendors(response.data.vendors);
      } catch (error) {
        console.error("Error searching vendors:", error);
        toast({
          content: "Failed to search vendors",
          toastType: "error",
        });
      }
    }, 300),
    [searchVendorsHttp, toast, cancelSearchVendors]
  );

  const {
    execute: addSocialHttp,
    fetching: addingSocial,
    cancel: cancelAddSocial,
  } = useAddSocialAdminHttp();

  const {
    execute: createPostHttp,
    fetching: creatingPost,
    cancel: cancelCreatePost,
  } = useCreatePostAdminHttp();

  const {
    execute: getInstagramPostDetails,
    fetching: fetchingInstagramPostDetails,
    cancel: cancelInstagramPostDetails,
  } = useGetInstagramPostDetailsAdminHttp();

  const form = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      posts: [
        {
          post_link: "",
          fixed_payout: undefined,
          vendorId: "",
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "posts",
  });

  async function addSocial(platform: string, handle: string) {
    try {
      const response = await addSocialHttp({
        body: {
          platform,
          handle,
        },
      });
      if (!response.success) {
        return false;
      }
      return true;
    } catch (error) {
      return false;
    }
  }

  const debouncedInput = useCallback(
    debounce(async (searchValue: string, index: number) => {
      cancelInstagramPostDetails();
      setPostDetails((prev) => {
        const newDetails = { ...prev };
        delete newDetails[index];
        return newDetails;
      });
      setSocialNotOnSolo((prev) => ({ ...prev, [index]: false }));
      const inputString = searchValue.trim();
      if (!inputString) {
        return;
      }
      fetchInstagramDetails(inputString, index);
    }, 500),
    []
  );

  async function fetchInstagramDetails(url: string, index: number) {
    if (!campaignId) return;
    const response = await getInstagramPostDetails({
      body: null,
      queryParams: { postUrl: url },
      pathParams: { campaignId },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setPostDetails((prev) => ({ ...prev, [index]: response.data }));

    // Handle the 3 cases for post status
    if (response.data.post) {
      const postStatus = response.data.post.postStatus.status;
      if (postStatus === PostStatus.vendorDraft) {
        // Case 1: Post exists and is vendor draft - populate fields
        form.setValue(`posts.${index}.vendorId`, response.data.post.user.id);
        form.setValue(
          `posts.${index}.fixed_payout`,
          response.data.post.fixedPayout.amount
        );
      }
      // Case 2: Post exists but is not vendor draft - will show error in UI
    }
    // Case 3: No post - allow normal flow

    if (response.data.social) {
      return setSocialNotOnSolo((prev) => ({ ...prev, [index]: false }));
    }
    return setSocialNotOnSolo((prev) => ({ ...prev, [index]: true }));
  }

  // Update the createPost function to return boolean
  async function createPost(data: PostData, index: number) {
    try {
      if (!campaignId) {
        return false;
      }
      const currentPostDetails = postDetails[index];
      const isNotOnSolo = socialNotOnSolo[index];

      // Check if post exists and is not vendor draft - prevent creation
      if (
        currentPostDetails?.post &&
        currentPostDetails.post.postStatus.status !== PostStatus.vendorDraft
      ) {
        toast({
          content:
            fields.length > 1
              ? `Post ${index + 1}: Post already exists with status ${currentPostDetails.post.postStatus.status}`
              : `Post already exists with status ${currentPostDetails.post.postStatus.status}`,
          toastType: "error",
        });
        return false;
      }

      if (isNotOnSolo && currentPostDetails?.instagramUserProfile?.username) {
        const socialSuccess = await addSocial(
          SocialPlatforms.instagram,
          currentPostDetails?.instagramUserProfile?.username
        );
        if (!socialSuccess) {
          return false;
        }
      }

      const response = await createPostHttp({
        body: {
          liveUrl: data.post_link,
          fixedPayoutAmount: data.fixed_payout,
          userId: data.vendorId, 
          campaignId,
        },
      });

      if (!response.success) {
        toast({
          content:
            fields.length > 1
              ? `Post ${index + 1}: ${response.error.message} `
              : response.error.message,
          toastType: "error",
        });
        return false;
      }
      return true;
    } catch (error) {
      return false;
    }
  }

  // Updated onSubmit function with individual post tracking
  async function onSubmit(data: FormData) {
    setIsSubmitting(true);
    setPostSuccess({}); // Reset success tracking

    try {
      for (let i = 0; i < data.posts.length; i++) {
        const success = await createPost(data.posts[i], i);

        if (success) {
          // Mark this post as successful
          setPostSuccess((prev) => ({ ...prev, [i]: true }));
        } else {
          // Stop the process on first failure
          setIsSubmitting(false);
          return;
        }
      }

      // All posts successful - cleanup
      document.getElementById("closeDialoug")?.click();
      form.reset();
      document.getElementById("getPosts")?.click();
      setPostDetails({});
      setSocialNotOnSolo({});
      setPostSuccess({});
      document.getElementById("closeSheet")?.click();
    } catch (error) {
      console.error("Error in onSubmit:", error);
    } finally {
      setIsSubmitting(false);
    }
  }

  const addPost = () => {
    if (fields.length < 10) {
      append({
        post_link: "",
        fixed_payout: undefined,
        vendorId: "",
      });
    }
  };

  const removePost = (index: number) => {
    if (fields.length > 1) {
      remove(index);
      // Clean up post details for removed index
      setPostDetails((prev) => {
        const newDetails = { ...prev };
        delete newDetails[index];
        return newDetails;
      });
      setSocialNotOnSolo((prev) => {
        const newSocials = { ...prev };
        delete newSocials[index];
        return newSocials;
      });
    }
  };


  // Check if fields should be disabled for vendor draft
  const shouldDisableFields = (index: number) => {
    const postDetail = postDetails[index];
    return (
      postDetail?.post &&
      postDetail.post.postStatus.status === PostStatus.vendorDraft
    );
  };

  // Check if post has error (exists but not vendor draft)
  const hasPostError = (index: number) => {
    const postDetail = postDetails[index];
    return (
      postDetail?.post &&
      postDetail.post.postStatus.status !== PostStatus.vendorDraft
    );
  };

  // Show form validation errors as toast
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
    if (form.formState.errors && !isSubmitting) {
      const errors = form.formState.errors;
      if (errors.posts && Array.isArray(errors.posts)) {
        const firstErrorIndex = errors.posts.findIndex((error) => error);
        if (firstErrorIndex !== -1) {
          const firstError = errors.posts[firstErrorIndex];
          const errorKey = Object.keys(firstError || {})[0];
          if (errorKey && firstError) {
            toast({
              content:
                fields.length > 1
                  ? `Post ${firstErrorIndex + 1}: ${firstError[errorKey as keyof typeof firstError]?.message}`
                  : firstError[errorKey as keyof typeof firstError]?.message,
              toastType: "error",
            });
          }
        }
      }
    }
  }, [form.formState.errors]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (reset) {
      form.reset();
      cancelInstagramPostDetails();
      cancelCreatePost();
      cancelAddSocial();
      setPostDetails({});
      setSocialNotOnSolo({});
      setPostSuccess({});
    }
  }, [reset, form]);

  // Watch for post link changes and fetch Instagram details
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name?.startsWith("posts.") && name.endsWith(".post_link")) {
        const index = Number.parseInt(name.split(".")[1]);
        const postLink = value.posts?.[index]?.post_link;

        if (postLink) {
          const isValidLink = postLink.match(
            /^(https?:\/\/)?(www\.)?(instagram\.com|instagr\.am)/
          );
          if (postLink.length > 0 && isValidLink) {
            debouncedInput(postLink, index);
          } else {
            setPostDetails((prev) => {
              const newDetails = { ...prev };
              delete newDetails[index];
              return newDetails;
            });
          }
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [debouncedInput, form]);

  return (
    <SheetContent className="flex flex-col p-0 border-border w-full max-w-2xl text-primary">
      <SheetTitle className="p-5 border-b border-border font-semibold text-2xl text-white">
        Add new posts
      </SheetTitle>

      <div className="flex flex-col flex-1 gap-6 overflow-y-auto">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-6 h-full"
          >
            <div className="flex flex-col flex-1 gap-8 p-5">
              {fields.map((field, index) => {
                const currentPostDetails = postDetails[index];
                const isNotOnSolo = socialNotOnSolo[index];
                const isFetchingDetails = fetchingInstagramPostDetails;
                const isPostSuccessful = postSuccess[index];
                const postError = hasPostError(index);

                return (
                  <div
                    key={field.id}
                    className={`relative flex flex-col gap-6 p-4 border rounded-lg transition-all duration-300 ${
                      isPostSuccessful
                        ? "border-green-500 bg-green-500/10"
                        : postError
                          ? "border-red-500 bg-red-500/10"
                          : "border-border"
                    }`}
                  >
                    {fields.length > 1 && !isPostSuccessful && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removePost(index)}
                        className="top-2 right-2 absolute hover:bg-transparent p-2 text-gray-400 hover:text-gray-400"
                        disabled={isSubmitting}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}

                    {fields.length > 1 && (
                      <div className="mb-2">
                        <h3 className="flex items-center gap-1.5 font-medium text-white">
                          {isPostSuccessful && (
                            <CheckIcon className="w-4 h-4" />
                          )}{" "}
                          Post {index + 1}
                        </h3>
                      </div>
                    )}

                    {/* Post Link */}
                    <FormInput
                      form={form}
                      name={`posts.${index}.post_link`}
                      label="Enter post link here"
                      placeholder="https://"
                      required
                      type="text"
                      disabled={
                        creatingPost ||
                        addingSocial ||
                        isFetchingDetails ||
                        isPostSuccessful
                      }
                    />

                    {/* Loading spinner for current post being processed */}
                    {isSubmitting &&
                      !isPostSuccessful &&
                      Object.keys(postSuccess).length === index && (
                        <div className="flex justify-center items-center gap-2 bg-tertiarybuttonbg p-4 border border-border rounded-lg">
                          <Spinner width={20} />
                          <span className="text-gray-400 text-sm">
                            {addingSocial
                              ? "Adding social..."
                              : "Adding post..."}
                          </span>
                        </div>
                      )}

                    {/* Post Details Display */}
                    {currentPostDetails && !isFetchingDetails && (
                      <>
                        <div className="flex items-center gap-1.5 bg-tertiarybuttonbg p-4 border-2 border-border rounded-lg">
                          <div className="border border-border rounded-full w-10 h-10" />
                          <div className="flex flex-col">
                            <p className="text-semibold">
                              {currentPostDetails.instagramUserProfile.username}
                            </p>
                            <p className="flex items-center gap-1 text-sm">
                              <InstagramIcon />
                              {millify(
                                currentPostDetails.instagramUserProfile
                                  .followersCount
                              )}
                            </p>
                          </div>
                        </div>

              
                      </>
                    )}

                    {/* Show vendor and payout fields only if no post error */}
                    {!isFetchingDetails && currentPostDetails && !postError && (
                      <>
                        {/* Vendor Selection */}
                        <FormSearch
                          form={form}
                          name={`posts.${index}.vendorId`}
                          label="Select vendor"
                          placeholder={
                            currentPostDetails.post?.postStatus.status ===
                            PostStatus.vendorDraft
                              ? `Selected: ${currentPostDetails.post.user.name}`
                              : "Search and select vendor"
                          }
                          options={
                            vendors?.map((vendor) => ({
                              value: vendor.id,
                              label: vendor.name,
                              data: vendor,
                            })) || []
                          }
                          onSearch={searchVendors}
                          fetching={searchingVendors}
                          showAllOnEmpty={true}
                          maxResultsWhileSearching={5}
                          emptyMessage="No vendors found"
                          disabled={
                            isPostSuccessful ||
                            shouldDisableFields(index) ||
                            false
                          }
                        />

                        {/* Fixed Payout */}
                        <FormField
                          control={form.control}
                          name={`posts.${index}.fixed_payout`}
                          render={({ field }) => (
                            <FormItem className="text-start">
                              <FormLabel className="font-semibold">
                                Enter fixed payout amount
                              </FormLabel>
                              <FormControl>
                                <div className="relative w-full">
                                  <span className="left-0 absolute inset-y-0 flex items-center pl-5 font-semibold text-xl">
                                    ₹
                                  </span>
                                  <Input
                                    {...field}
                                    className="py-6 pl-10 w-full h-16 placeholder:text-secondary"
                                    type="number"
                                    placeholder="Enter fixed payout amount"
                                    min={0}
                                    disabled={
                                      isPostSuccessful ||
                                      shouldDisableFields(index) ||
                                      false
                                    }
                                  />
                                </div>
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </>
                    )}

                    {/* Social not on Solo warning */}
                    {isNotOnSolo &&
                      !isFetchingDetails &&
                      !currentPostDetails?.post && (
                        <div className="flex items-center">
                          <ExclamationIcon />
                          <span className="text-sm text-yellow-500">
                            Not on solo
                          </span>
                        </div>
                      )}
                  </div>
                );
              })}

              {/* Add More Button */}
              {fields.length < 10 && !isSubmitting && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={addPost}
                  className="flex items-center gap-2 hover:bg-white/5 p-4 border border-border rounded-lg text-gray-400 hover:text-white"
                >
                  <Plus className="w-4 h-4" />
                  Add more post ({fields.length}/10)
                </Button>
              )}
            </div>

            {/* Submit Button */}
            {fields.some((_, index) => postDetails[index]) &&
            !fetchingInstagramPostDetails ? (
              <div className="bottom-0 sticky bg-background p-5 border-t border-border">
                <Button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    Object.keys(postSuccess).length === fields.length ||
                    fields.some((_, index) => hasPostError(index))
                  }
                  className="bg-primarybuttonbg w-full h-14 font-semibold text-primarybuttontext tracking-wider"
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <Spinner width={15} />
                      Processing posts... ({Object.keys(postSuccess).length}/
                      {fields.length})
                    </div>
                  ) : Object.keys(postSuccess).length === fields.length ? (
                    "All posts added successfully ✓"
                  ) : fields.some((_, index) => hasPostError(index)) ? (
                    "Cannot proceed - Post conflicts detected"
                  ) : fields.some((_, index) => socialNotOnSolo[index]) ? (
                    "Add socials & posts →"
                  ) : (
                    `Add ${fields.length} post${fields.length > 1 ? "s" : ""} →`
                  )}
                </Button>
              </div>
            ) : fetchingInstagramPostDetails ? (
              <div className="bottom-0 sticky bg-background p-5 border-t border-border">
                <Button
                  type="button"
                  disabled={fetchingInstagramPostDetails}
                  className="gap-2 bg-tertiarybuttonbg w-full h-14 font-semibold text-primarybuttontext text-white tracking-wider"
                >
                  <Spinner width={15} /> Fetching details
                </Button>
              </div>
            ) : null}
          </form>
        </Form>
      </div>
    </SheetContent>
  );
};
